/**
 * V2 List page — /user/{username}/list/{slug} (M8).
 *
 * One page serves both audiences: visitors get the shareable
 * public/unlisted view (title, byline, notes, places), the owner gets
 * the tools on top — edit title/description/visibility, per-entry
 * notes, up/down reorder (buttons, not drag — iPad first), remove,
 * delete. RLS keeps private lists owner-only; a private URL opened by
 * anyone else reads as not-found on purpose.
 */
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { icon } from '../../ui/icons.js';
import { shareButton, handleShareClick, shareUrl, setUrlParam, getUrlParam } from '../../ui/share.js';
import { shareMenu } from '../../ui/share-menu.js';
import { embedButton, handleEmbedClick, isEmbed } from '../../ui/embed.js';
import { showToast } from '../../ui/toast.js';
import { invalidate } from '../../core/query.js';
import * as gateway from '../../core/gateway.js';
import { mountMap, mapsAvailable, rowsInArea } from '../../ui/map-view.js';
import { searchGooglePlaces, createLocationFromPlace, mapsLinkCandidates } from '../log/places.js';
import { socialLinkLabel } from '../yoink/yoink.js';
import {
  fetchListPage, updateList, deleteList, updateEntryNote, removeFromList, moveEntry, addToList, normalizeUrl,
} from './lists.js';
import {
  fetchListVisits, fetchMyListFollowIds, followList, unfollowList,
  WANT_TO_GO, sweepVisitedFromWantToGo,
} from './lists.js';
import { loadCategoryField, categoryFieldHtml, resolveCategoryId } from './category-field.js';
import { setContext as setRibbonContext } from '../../ui/visitor-ribbon.js';

const VISIBILITY_BADGE = {
  private: 'Private — only you can see this list',
  unlisted: 'Unlisted — anyone with the link can see it',
  public: 'Public',
};

// List health (2026-08-03): the place page's 3-day metadata refresh
// persists locations.status; lists surface what it saw. Everyone gets
// the row stamp — a friend planning from a shared list deserves the
// warning too.
const CLOSED_STAMP = {
  closed_permanently: '<span class="list-entry-closed">Permanently closed</span>',
  closed_temporarily: '<span class="list-entry-closed list-entry-closed-temp">Temporarily closed</span>',
};

function entryRow(entry, index, count, { isOwner, editingNote, visited, canReorder = true }) {
  const loc = entry.locations || {};
  const note = entry.note || '';
  const noteBlock = editingNote
    ? `
      <div class="list-note-edit">
        <textarea data-note-input rows="2" maxlength="500" aria-label="Note about this place"
          placeholder="A note about this place…">${escapeHtml(note)}</textarea>
        <div class="list-note-actions">
          <button type="button" class="v2-btn-more" data-note-save="${entry.location_id}">Save note</button>
          <button type="button" class="v2-btn-more" data-note-cancel>Cancel</button>
        </div>
      </div>`
    : note ? `<p class="list-entry-note">“${escapeHtml(note)}”</p>` : '';
  return `
    <li class="list-entry${visited ? ' list-entry-done' : ''}">
      <div class="list-entry-main" data-entry-loc="${loc.id || ''}" role="button" tabindex="0"
        aria-label="${escapeHtml(loc.display_name || 'A place')}${visited ? ' — you’ve been here' : ''}">
        <span class="list-entry-rank">${index + 1}</span>
        <span class="list-entry-body">
          <span class="list-entry-name">${escapeHtml(loc.display_name || 'A place no longer on the map')}</span>
          ${loc.formatted_address ? `<span class="list-entry-addr">${escapeHtml(loc.formatted_address)}</span>` : ''}
          ${visited ? '<span class="list-entry-visited">✓ Been here</span>' : ''}
          ${CLOSED_STAMP[loc.status] || ''}
        </span>
      </div>
      ${noteBlock}
      ${isOwner ? `
      <div class="list-entry-tools">
        ${canReorder ? `
        <button type="button" class="v2-btn-more" data-entry-move="up" data-entry-index="${index}"
          ${index === 0 ? 'disabled' : ''} aria-label="Move up">↑</button>
        <button type="button" class="v2-btn-more" data-entry-move="down" data-entry-index="${index}"
          ${index === count - 1 ? 'disabled' : ''} aria-label="Move down">↓</button>` : ''}
        <button type="button" class="v2-btn-more" data-entry-note-edit="${entry.location_id}">
          ${note ? 'Edit note' : 'Add note'}</button>
        <button type="button" class="v2-btn-more list-entry-remove" data-entry-remove="${entry.location_id}">Remove</button>
      </div>` : ''}
    </li>`;
}

function editDialog(list, confirmingDelete, categoryCats) {
  return `
    <dialog class="v2-auth-dialog list-edit-dialog" data-list-edit-dialog>
      <h2 class="v2-dialog-title">Edit list</h2>
      <form data-list-edit-form>
        <input type="text" name="title" aria-label="List name"
          value="${escapeHtml(list.title)}" maxlength="80" required>
        <textarea name="description" rows="2" maxlength="500" aria-label="What’s this list about?"
          placeholder="What’s this list about? (optional)">${escapeHtml(list.description || '')}</textarea>
        <input type="url" name="url" aria-label="Link for this list" value="${escapeHtml(list.url || '')}"
          placeholder="Link (optional) — Instagram, TikTok, your blog…" maxlength="500" autocomplete="off">
        ${categoryFieldHtml(categoryCats, list.category_id || null, '-edit')}
        <label class="mylist-vis-label">Who can see it?
          <select name="visibility">
            <option value="private" ${list.visibility === 'private' ? 'selected' : ''}>Private — only you</option>
            <option value="unlisted" ${list.visibility === 'unlisted' ? 'selected' : ''}>Unlisted — anyone with the link</option>
            <option value="public" ${list.visibility === 'public' ? 'selected' : ''}>Public — shown on your journal</option>
          </select>
        </label>
        <button type="submit" class="v2-btn-leather">Save changes</button>
      </form>
      <button type="button" class="v2-btn-more list-delete-btn" data-list-delete>
        ${confirmingDelete ? 'Really delete this list?' : 'Delete list'}</button>
      <button type="button" class="v2-btn-dialog-close" data-list-edit-close>Close</button>
    </dialog>`;
}

// A private list's link only opens for its owner — sharing one sends
// friends to "No list here" and a blank preview card (user report
// 2026-07-20). So the owner's Share tap on a private list asks first.
function sharePrivateDialog() {
  return `
    <dialog class="v2-auth-dialog list-share-dialog" data-list-share-dialog>
      <h2 class="v2-dialog-title">This list is private</h2>
      <p class="list-share-note">Right now the link only works for you — anyone else
        who opens it sees “No list here.”</p>
      <button type="button" class="v2-btn-leather" data-share-make-unlisted>
        Make it unlisted &amp; share</button>
      <p class="log-hint">Unlisted means anyone with the link can see it,
        but it won’t show on your journal.</p>
      <button type="button" class="v2-btn-more" data-share-private-anyway>Share it anyway</button>
      <button type="button" class="v2-btn-dialog-close" data-share-dialog-close>Cancel</button>
    </dialog>`;
}

export function mount(el, ctx) {
  const { store, router, params } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;
  let data = null; // { owner, list, entries }
  let editingNoteId = null;
  let confirmingDelete = false;
  let editOpen = false;
  let categoryCats = null; // taggable catalog for the edit dialog (lazy; null = field hidden)
  let categoryCatsTried = false;

  // Fetched on the first edit-dialog open, not at mount — the catalog and
  // the availability probe would be dead weight for every non-owner view.
  async function loadEditCategoryField() {
    if (categoryCatsTried) return;
    categoryCatsTried = true;
    categoryCats = await loadCategoryField(); // null pre-migration, never throws
    if (!destroyed && editOpen && categoryCats) render();
  }
  // Shared links keep the view; an embed leads with the map (that's the
  // thing being put on the other site) unless the snippet was hand-tuned
  // to ?view=list.
  let viewMode = getUrlParam('view') === 'map' || (isEmbed() && getUrlParam('view') !== 'list')
    ? 'map' : 'list';
  let mapHandle = null;
  // The map's viewport carries back into the list (2026-08-04 on-device
  // ask, same idiom as Discover's map↔cards continuity): pan/zoom the
  // Map tab, flip to List, see that area only. null until the map moves.
  let mapArea = null;
  let addOpen = false; // owner's "Add a place" search (user ask 2026-07-09)
  let addBusy = false;
  let sharePromptOpen = false; // owner tapped Share on a private list
  let visitedIds = new Set(); // the viewer's yoinked places on this list
  let followedListIds = null; // null = signed out or list_follows not applied
  let followBusy = false;

  function isOwner() {
    return !!data && store.get().user?.id === data.owner.id;
  }

  // The viewer's own layer on top of the shared page: which of these
  // places they've yoinked (progress — backdated visits count, see
  // fetchListVisits) and whether this list is on their shelf. Both
  // degrade to "absent" rather than blocking the page.
  async function loadViewerState() {
    if (!data || !store.get().user) {
      visitedIds = new Set();
      followedListIds = null;
      return;
    }
    [visitedIds, followedListIds] = await Promise.all([
      fetchListVisits(store, data.list.id, data.entries.map((e) => e.location_id))
        .catch(() => new Set()),
      isOwner() ? Promise.resolve(null) : fetchMyListFollowIds(store),
    ]);
  }

  /** "You've been to X of Y" — the reason to come back to someone's list. */
  function progressHtml() {
    // Not on "Want to go": going takes the place OFF the list (user rule
    // 2026-08-06, the sweep below), so a bar here would always read 0%.
    if (data.list.kind === WANT_TO_GO.kind) return '';
    if (!store.get().user || !data.entries.length) return '';
    const total = data.entries.length;
    const done = data.entries.filter((e) => visitedIds.has(e.location_id)).length;
    const pct = Math.round((done / total) * 100);
    return `
      <section class="set-progress list-progress" aria-label="Your progress">
        <div class="set-progress-head">
          <h3 class="friends-title">Your progress</h3>
          <span class="set-progress-label">${done} of ${total} (${pct}%)</span>
        </div>
        <div class="collection-bar set-progress-bar"><span class="collection-fill" style="width:${pct}%"></span></div>
        ${done === total ? '<p class="list-progress-done">You’ve been everywhere on this list.</p>' : ''}
      </section>`;
  }

  function render() {
    if (!data) return;
    const { owner, list, entries } = data;
    setRibbonContext(`A list by ${owner.full_name || owner.username}. Save places worth going — and make your own.`);
    const ownerView = isOwner();
    const who = owner.full_name || owner.username;
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back">
        <a href="${basePath}/user/${encodeURIComponent(owner.username)}" data-back-owner>← ${escapeHtml(who)}</a>
        <span class="yoink-share-row">
          ${shareMenu(
            list.visibility !== 'private' && entries.length ? embedButton(list.title) : '',
            shareButton(list.title)
          )}
        </span>
      </nav>
      <article class="v2-list-page">
        <header class="list-head">
          ${icon('bookmark', 26)}
          <div>
            <h2 class="v2-page-title">${escapeHtml(list.title)}</h2>
            <p class="list-byline">by ${escapeHtml(who)} · ${entries.length} place${entries.length === 1 ? '' : 's'}${list.featured
              ? ' · <span class="featured-star" title="Picked by the rangers">★ Featured</span>' : ''}</p>
          </div>
          ${ownerView ? '<button type="button" class="v2-btn-more" data-list-edit>Edit</button>'
          : store.get().user && list.visibility !== 'private' && followedListIds !== null ? `
          <button type="button" class="v2-btn-more${followedListIds.includes(list.id) ? ' list-following' : ''}"
            data-list-follow ${followBusy ? 'disabled' : ''}>
            ${followedListIds.includes(list.id) ? '✓ Following' : 'Follow'}</button>` : ''}
        </header>
        ${ownerView && list.visibility !== 'public' ? `
        <p class="list-vis-badge">${VISIBILITY_BADGE[list.visibility]}</p>` : ''}
        ${(() => {
          const gone = entries.filter((e) => e.locations?.status === 'closed_permanently').length;
          if (!ownerView || !gone) return '';
          return `<p class="list-health-note">${gone === 1
            ? 'A place on this list has closed for good.'
            : `${gone} places on this list have closed for good.`} Their Remove buttons are below.</p>`;
        })()}
        ${list.description ? `<p class="list-desc">${escapeHtml(list.description)}</p>` : ''}
        ${list.url && socialLinkLabel(list.url) ? `
        <a class="v2-btn-more yoink-social list-link" href="${escapeHtml(list.url)}"
          target="_blank" rel="noopener">${escapeHtml(socialLinkLabel(list.url))} ↗</a>` : ''}
        ${progressHtml()}
        ${entries.length ? `
        <div class="v2-toggles">
          <div class="v2-scope" role="group" aria-label="View">
            <button class="v2-scope-btn ${viewMode === 'list' ? 'scope-active' : ''}"
              data-list-view="list" aria-pressed="${viewMode === 'list'}">List</button>
            <button class="v2-scope-btn ${viewMode === 'map' ? 'scope-active' : ''}"
              data-list-view="map" aria-pressed="${viewMode === 'map'}">Map</button>
          </div>
        </div>` : ''}
        ${ownerView && viewMode === 'list' ? `
        <div class="list-add-place">
          ${addOpen ? `
          <label class="v2-field">Add a place
            <input type="search" data-add-place-input placeholder="Search any place — or paste a Google Maps link"
              autocomplete="off" enterkeyhint="search">
          </label>
          <div class="log-place-results" data-add-place-results aria-live="polite"></div>
          <button type="button" class="v2-btn-more" data-add-place-close>Done adding</button>`
          : '<button type="button" class="v2-btn-more" data-add-place-open>+ Add a place</button>'}
          <button type="button" class="list-import-link" data-import-google>
            Import a list from Google Maps</button>
        </div>` : ''}
        ${(() => {
          if (viewMode === 'map' && entries.length) {
            return `
        <div class="v2-map" data-list-map></div>
        <p class="log-hint">Tap a pin for the place — the list keeps to this view when you flip back.</p>`;
          }
          if (!entries.length) {
            return `<p class="v2-empty">Nothing saved here yet.${ownerView ? ' Tap “+ Add a place” above, or “Want to go” on any place page.' : ''}</p>`;
          }
          // Map↔list continuity: original positions ride along so ranks
          // stay honest while the view narrows; reordering pauses (moving
          // row 3 next to row 214 has no meaning in a filtered slice).
          const pairs = entries.map((entry, i) => ({ entry, i }));
          const visible = mapArea ? pairs.filter(({ entry }) => rowsInArea([entry], mapArea).length) : pairs;
          const filtering = !!mapArea && visible.length < pairs.length;
          const shown = filtering ? visible : pairs;
          return `
        ${filtering ? `<p class="log-hint list-area-line">Showing ${visible.length} of ${entries.length}
          in the map’s view.
          <button type="button" class="v2-btn-more" data-list-area-clear>Show all</button></p>` : ''}
        ${filtering && !visible.length
          ? '<p class="v2-empty">None of these places are in the map’s view — pan wider, or show all.</p>'
          : `
        <ol class="list-entries">
          ${shown.map(({ entry, i }) => entryRow(entry, i, entries.length, {
            isOwner: ownerView,
            editingNote: editingNoteId === entry.location_id,
            visited: visitedIds.has(entry.location_id),
            canReorder: !filtering,
          })).join('')}
        </ol>`}`;
        })()}
        ${ownerView ? editDialog(list, confirmingDelete, categoryCats) : ''}
        ${ownerView && list.visibility === 'private' ? sharePrivateDialog() : ''}
      </article>`;
    if (editOpen) el.querySelector('[data-list-edit-dialog]')?.showModal();
    if (sharePromptOpen) el.querySelector('[data-list-share-dialog]')?.showModal();
    mapHandle?.cleanup();
    mapHandle = null;
    const mapHost = el.querySelector('[data-list-map]');
    if (mapHost) {
      // The whole list on one board, framed to its pins. Entries carry
      // their place's newest yoink photo (fetchListPage's embedded
      // yoinks) — surfaced as `images` so the popup derivation reads
      // them the same way it reads real yoink rows.
      mapHandle = mountMap(mapHost, {
        store,
        router,
        fitToMarkers: true,
        onBoundsChange: (bounds) => { mapArea = bounds; },
        // These pins are saved entries, not logs — "1 yoink" here
        // contradicted the place page's honest 0 (on-device 2026-08-04)
        countNoun: null,
      });
      if (mapsAvailable()) {
        mapHandle.setYoinks(entries.map((entry) => ({
          ...entry,
          images: entry.locations?.yoinks?.[0]?.images || null,
        })));
      }
    }
    if (addOpen) el.querySelector('[data-add-place-input]')?.focus();
  }

  function renderMissing() {
    el.innerHTML = `
      <nav class="v2-back" aria-label="Back"><a href="${basePath}/" data-route="discover">← Discover</a></nav>
      <div class="v2-empty">No list here — it may be private or gone.</div>`;
  }

  /**
   * Opening your own "Want to go" crosses off what you've already done:
   * any entry the visits query says you've yoinked comes straight off
   * the list (backdated logs and imports included — the log flow's live
   * cross-off can't catch those). Owner only; on failure the entries
   * simply stay for the next visit's sweep.
   */
  async function sweepWantToGo() {
    if (!data || !isOwner() || data.list.kind !== WANT_TO_GO.kind) return;
    const visited = data.entries.filter((e) => visitedIds.has(e.location_id));
    if (!visited.length) return;
    try {
      await sweepVisitedFromWantToGo(store, data.list.id, visited.map((e) => e.location_id));
      data.entries = data.entries.filter((e) => !visitedIds.has(e.location_id));
      showToast(visited.length === 1
        ? 'Crossed off a place you’ve been ✓'
        : `Crossed off ${visited.length} places you’ve been ✓`, { type: 'success' });
    } catch { /* leave them listed — nothing on the page depends on the sweep */ }
  }

  async function load() {
    el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
    try {
      data = await fetchListPage(params.username, params.slug);
      if (destroyed) return;
      if (!data) {
        renderMissing();
        return;
      }
      await loadViewerState();
      if (destroyed) return;
      await sweepWantToGo();
      if (destroyed) return;
      document.title = `${data.list.title} | Yoink Adventures v2`;
      render();
    } catch {
      if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t reach the journal.</div>';
    }
  }

  // ------------------------------------------------------------------
  // "+ Add a place" (owner) — search the journal's places AND Google, so
  // un-yoinked spots can join a list (user ask 2026-07-09)
  // ------------------------------------------------------------------

  const inList = (locationId) => data.entries.some((e) => e.location_id === locationId);

  async function runAddSearch(term) {
    const resultsEl = el.querySelector('[data-add-place-results]');
    if (!resultsEl) return;
    if (term.trim().length < 2) {
      resultsEl.innerHTML = '';
      return;
    }
    if (/^https?:\/\//i.test(term.trim())) {
      runLinkResolve(term.trim(), resultsEl);
      return;
    }
    resultsEl.innerHTML = '<div class="log-hint">Searching…</div>';
    const [db, google] = await Promise.all([
      gateway.rawQuery(gateway.TABLES.LOCATIONS, {
        select: 'id,display_name,formatted_address',
        filters: { display_name: `ilike.*${term.trim()}*` },
        limit: 5,
      }).then((r) => r.data || []).catch(() => []),
      searchGooglePlaces(term.trim()).catch(() => []),
    ]);
    if (destroyed) return;
    const stale = el.querySelector('[data-add-place-input]')?.value.trim() !== term.trim();
    if (stale) return; // a newer search already owns the results box
    const dbRows = db.filter((l) => !inList(l.id)).map((l) => `
      <button type="button" class="log-place-option" data-add-existing="${l.id}">
        <strong>${escapeHtml(l.display_name)}</strong>
        ${l.formatted_address ? `<span>${escapeHtml(l.formatted_address)}</span>` : ''}
      </button>`);
    const googleRows = google.slice(0, 5).map((p, i) => `
      <button type="button" class="log-place-option" data-add-google="${i}"
        data-add-place-id="${escapeHtml(p.placeId)}" data-add-main="${escapeHtml(p.mainText)}">
        <strong>${escapeHtml(p.mainText)}</strong>
        ${p.secondaryText ? `<span>${escapeHtml(p.secondaryText)}</span>` : ''}
        <span class="list-add-badge">Google</span>
      </button>`);
    resultsEl.innerHTML = [...dbRows, ...googleRows].join('') ||
      '<div class="log-hint">Nothing by that name — try another spelling.</div>';
  }

  // A pasted Maps link resolves to ONE confident match to confirm — a
  // card, not a results page. Alternates stay folded behind "Not the
  // right place?" so the common case is paste → one tap.
  async function runLinkResolve(link, resultsEl) {
    resultsEl.innerHTML = '<div class="log-hint">Reading your Maps link…</div>';
    let result;
    try { result = await mapsLinkCandidates(link); } catch { result = null; }
    if (destroyed) return;
    if (el.querySelector('[data-add-place-input]')?.value.trim() !== link) return; // a newer input owns the box
    if (result?.rejected) {
      resultsEl.innerHTML =
        '<div class="log-hint">That link points to a whole area, not a specific place — try linking the place itself.</div>';
      return;
    }
    if (!result?.candidates?.length) {
      resultsEl.innerHTML =
        '<div class="log-hint">Couldn’t read that link — try searching by name instead.</div>';
      return;
    }
    const [best, ...alternates] = result.candidates;
    const listed = data.entries.some((e) => e.locations?.google_place_id === best.placeId);
    resultsEl.innerHTML = `
      <div class="list-link-match">
        <div class="log-place-option list-link-best">
          <strong>${escapeHtml(best.mainText)}</strong>
          ${best.secondaryText ? `<span>${escapeHtml(best.secondaryText)}</span>` : ''}
          <span class="list-add-badge">From your link</span>
        </div>
        ${listed ? '<div class="log-hint">Already on this list.</div>' : `
        <button type="button" class="v2-btn-leather list-link-add" data-add-google
          data-add-place-id="${escapeHtml(best.placeId)}" data-add-main="${escapeHtml(best.mainText)}">
          Add to this list</button>`}
        ${alternates.length ? `
        <button type="button" class="list-link-alt-toggle" data-link-alternates>
          Not the right place? Show other matches</button>
        <div class="log-place-results" data-link-alt-rows hidden>
          ${alternates.map((p) => `
          <button type="button" class="log-place-option" data-add-google
            data-add-place-id="${escapeHtml(p.placeId)}" data-add-main="${escapeHtml(p.mainText)}">
            <strong>${escapeHtml(p.mainText)}</strong>
            ${p.secondaryText ? `<span>${escapeHtml(p.secondaryText)}</span>` : ''}
          </button>`).join('')}
          <div class="log-hint">Still not it? Try searching by name.</div>
        </div>` : '<div class="log-hint">Not the right place? Try searching by name.</div>'}
      </div>`;
  }

  async function addPlace(locationId, name) {
    if (addBusy) return;
    addBusy = true;
    try {
      await addToList(data.list.id, locationId);
      invalidate('list-page:');
      data = await fetchListPage(params.username, params.slug);
      await loadViewerState(); // the new place may already be yoinked
      if (store.get().wantToGoListId === data.list.id) {
        store.set({ savedLocationIds: [...new Set([...(store.get().savedLocationIds || []), locationId])] });
      }
      showToast(`${name} added.`, { type: 'success' });
      if (!destroyed) render();
    } catch {
      showToast('Couldn’t add that place — try again.');
    } finally {
      addBusy = false;
    }
  }

  async function addGooglePlace(button) {
    if (addBusy) return;
    button.disabled = true;
    try {
      const result = await createLocationFromPlace({
        placeId: button.dataset.addPlaceId,
        mainText: button.dataset.addMain,
      });
      if (result.rejected) {
        showToast('That’s a whole area, not a place — pick something more specific.');
        button.disabled = false;
        return;
      }
      await addPlace(result.location.id, result.location.display_name || button.dataset.addMain);
    } catch {
      showToast('Couldn’t add that place — try again.');
      if (button.isConnected) button.disabled = false;
    }
  }

  async function onSubmit(event) {
    const form = event.target.closest('[data-list-edit-form]');
    if (!form) return;
    event.preventDefault();
    const fields = form.elements;
    const patch = {
      title: fields.title.value.trim(),
      description: fields.description.value.trim() || null,
      url: normalizeUrl(fields.url?.value),
      visibility: fields.visibility.value,
    };
    if (!patch.title) return;
    // category_id rides only when the field rendered (column applied) —
    // a pre-migration PATCH carrying it would 400 the whole save
    if (categoryCats) {
      const categoryId = resolveCategoryId(categoryCats, fields.category?.value);
      if (categoryId === undefined) {
        showToast('That category didn’t match — pick one from the suggestions, or leave it blank.');
        return;
      }
      patch.category_id = categoryId;
    }
    try {
      const updated = await updateList(data.list.id, patch);
      if (updated) data.list = updated;
      editOpen = false;
      showToast('List updated.', { type: 'success' });
      render();
    } catch {
      showToast('Couldn’t save the changes — try again.');
    }
  }

  async function onActivate(event) {
    if (handleEmbedClick(event)) return;
    if (event.target.closest('.v2-btn-share') && isOwner() && data.list.visibility === 'private') {
      sharePromptOpen = true;
      render();
      return;
    }
    if (handleShareClick(event)) return;

    if (event.target.closest('[data-share-make-unlisted]')) {
      try {
        const updated = await updateList(data.list.id, { visibility: 'unlisted' });
        if (updated) data.list = updated;
        else data.list.visibility = 'unlisted';
        sharePromptOpen = false;
        showToast('Anyone with the link can see this list now.', { type: 'success' });
        render();
        await shareUrl(data.list.title);
      } catch {
        showToast('Couldn’t update the list — try again.');
      }
      return;
    }
    if (event.target.closest('[data-share-private-anyway]')) {
      sharePromptOpen = false;
      render();
      await shareUrl(data.list.title);
      return;
    }
    if (event.target.closest('[data-share-dialog-close]')) {
      sharePromptOpen = false;
      el.querySelector('[data-list-share-dialog]')?.close();
      return;
    }

    if (event.target.closest('[data-list-area-clear]')) {
      mapArea = null;
      render();
      return;
    }
    const follow = event.target.closest('[data-list-follow]');
    if (follow) {
      if (followBusy || followedListIds === null) return;
      followBusy = true;
      const wasFollowing = followedListIds.includes(data.list.id);
      try {
        if (wasFollowing) {
          await unfollowList(store, data.list.id);
          followedListIds = followedListIds.filter((id) => id !== data.list.id);
          showToast('Taken off your lists.', { type: 'success' });
        } else {
          await followList(store, data.list.id);
          followedListIds = [...followedListIds, data.list.id];
          showToast('On your lists now — find it under “Lists you follow”.', { type: 'success' });
        }
      } catch {
        showToast('Couldn’t update — try again.');
      } finally {
        followBusy = false;
        render();
      }
      return;
    }

    const view = event.target.closest('[data-list-view]');
    if (view) {
      if (view.dataset.listView !== viewMode) {
        viewMode = view.dataset.listView;
        setUrlParam('view', viewMode === 'map' ? 'map' : '');
        render();
      }
      return;
    }
    if (event.target.closest('[data-import-google]')) {
      router.navigate('lists-import');
      return;
    }
    if (event.target.closest('[data-add-place-open]')) {
      addOpen = true;
      render();
      return;
    }
    if (event.target.closest('[data-add-place-close]')) {
      addOpen = false;
      render();
      return;
    }
    const addExisting = event.target.closest('[data-add-existing]');
    if (addExisting) {
      addPlace(addExisting.dataset.addExisting,
        addExisting.querySelector('strong')?.textContent || 'Place');
      return;
    }
    const altToggle = event.target.closest('[data-link-alternates]');
    if (altToggle) {
      // DOM-only reveal — a re-render would wipe the pasted link
      el.querySelector('[data-link-alt-rows]')?.removeAttribute('hidden');
      altToggle.hidden = true;
      return;
    }
    const addGoogle = event.target.closest('[data-add-google]');
    if (addGoogle) {
      addGooglePlace(addGoogle);
      return;
    }

    const backOwner = event.target.closest('[data-back-owner]');
    if (backOwner) {
      event.preventDefault();
      router.navigate('user', { username: data.owner.username });
      return;
    }
    if (event.target.closest('[data-list-edit]')) {
      editOpen = true;
      confirmingDelete = false;
      loadEditCategoryField();
      render();
      return;
    }
    if (event.target.closest('[data-list-edit-close]')) {
      editOpen = false;
      confirmingDelete = false;
      el.querySelector('[data-list-edit-dialog]')?.close();
      return;
    }
    const deleteButton = event.target.closest('[data-list-delete]');
    if (deleteButton) {
      if (!confirmingDelete) {
        // Swap the label in place — a full render would wipe what's
        // typed in the open edit form
        confirmingDelete = true;
        deleteButton.textContent = 'Really delete this list?';
        return;
      }
      try {
        await deleteList(store, data.list.id);
        showToast('List deleted.', { type: 'success' });
        router.navigate('lists');
      } catch {
        showToast('Couldn’t delete the list — try again.');
      }
      return;
    }

    const move = event.target.closest('[data-entry-move]');
    if (move) {
      const index = Number(move.dataset.entryIndex);
      const direction = move.dataset.entryMove === 'up' ? -1 : 1;
      try {
        data.entries = await moveEntry(data.list.id, data.entries, index, direction);
        render();
      } catch {
        showToast('Couldn’t reorder — try again.');
      }
      return;
    }
    const noteEdit = event.target.closest('[data-entry-note-edit]');
    if (noteEdit) {
      editingNoteId = noteEdit.dataset.entryNoteEdit;
      render();
      return;
    }
    if (event.target.closest('[data-note-cancel]')) {
      editingNoteId = null;
      render();
      return;
    }
    const noteSave = event.target.closest('[data-note-save]');
    if (noteSave) {
      const locationId = noteSave.dataset.noteSave;
      const note = el.querySelector('[data-note-input]')?.value.trim() || '';
      try {
        await updateEntryNote(data.list.id, locationId, note);
        const entry = data.entries.find((e) => e.location_id === locationId);
        if (entry) entry.note = note || null;
        editingNoteId = null;
        render();
      } catch {
        showToast('Couldn’t save the note — try again.');
      }
      return;
    }
    const remove = event.target.closest('[data-entry-remove]');
    if (remove) {
      const locationId = remove.dataset.entryRemove;
      try {
        await removeFromList(data.list.id, locationId);
        data.entries = data.entries.filter((e) => e.location_id !== locationId);
        if (store.get().wantToGoListId === data.list.id) {
          store.set({
            savedLocationIds: (store.get().savedLocationIds || []).filter((id) => id !== locationId),
          });
        }
        render();
      } catch {
        showToast('Couldn’t remove that place — try again.');
      }
      return;
    }
    const entryMain = event.target.closest('[data-entry-loc]');
    if (entryMain?.dataset.entryLoc) {
      router.navigate('location', { id: entryMain.dataset.entryLoc });
    }
  }

  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.closest('[data-entry-loc]') || event.target.closest('textarea')) return;
    event.preventDefault();
    onActivate(event);
  }

  // Auth can settle after first paint. A user change re-fetches (RLS
  // decides what this viewer can see — your own private list is
  // invisible until your session is live), which also flips the owner
  // tools on or off.
  let seenUserId = store.get().user?.id || null;
  const unsubscribe = store.subscribe(() => {
    if (destroyed) return;
    const uid = store.get().user?.id || null;
    if (uid === seenUserId) return;
    seenUserId = uid;
    invalidate('list-page:');
    editingNoteId = null;
    editOpen = false;
    confirmingDelete = false;
    sharePromptOpen = false;
    load();
  });

  const onAddInput = debounce((event) => {
    if (!event.target.closest('[data-add-place-input]')) return;
    runAddSearch(event.target.value);
  }, 300);

  el.addEventListener('click', onActivate);
  el.addEventListener('submit', onSubmit);
  el.addEventListener('keydown', onKeydown);
  el.addEventListener('input', onAddInput);
  load();

  return () => {
    destroyed = true;
    unsubscribe();
    mapHandle?.cleanup();
    el.removeEventListener('click', onActivate);
    el.removeEventListener('submit', onSubmit);
    el.removeEventListener('keydown', onKeydown);
    el.removeEventListener('input', onAddInput);
  };
}
